import { useState, type KeyboardEvent, type MouseEvent } from 'react'
import { formatDate } from '@/common/lib/dateUtils'
import { CollapsibleCard } from '@/common/ui/CollapsibleCard'
import { ExpandableText } from '@/common/ui/ExpandableText'
import { Button } from '@/common/ui/Form/Button'

export type MarketplaceTextsSelection = {
    title: string
    description: string
}

type ProductImportCollapsibleProps = {
    productName: string
    productDescription: string
    importUpdateDate?: string | null
    selectionMode?: boolean
    onSelect?: (data: MarketplaceTextsSelection) => void
}

export const ProductImportCollapsible = ({
    productName,
    productDescription,
    importUpdateDate,
    selectionMode = false,
    onSelect,
}: ProductImportCollapsibleProps) => {
    const [isOpen, setIsOpen] = useState(false)
    const trimmedName = productName.trim()
    const trimmedDescription = productDescription.trim()
    const canSelect = Boolean(trimmedName || trimmedDescription)

    const handleSelect = () => {
        if (!canSelect || !onSelect) {
            return
        }

        onSelect({
            title: trimmedName,
            description: trimmedDescription,
        })
    }

    const handleSelectClick = (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation()
        handleSelect()
    }

    const handleSelectKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
        event.stopPropagation()
    }

    return (
        <CollapsibleCard
            title='Данные импорта'
            isOpen={isOpen}
            onToggle={setIsOpen}
            ariaLabel='Данные импорта товара'
            description={importUpdateDate ? `Обновление от ${formatDate(importUpdateDate)}` : undefined}
            className='w-full'
            headerAction={
                selectionMode ? (
                    <Button
                        type='button'
                        size='md'
                        variant='outline'
                        onClick={handleSelectClick}
                        onKeyDown={handleSelectKeyDown}
                        aria-label='Выбрать данные импорта'
                    >
                        Выбрать
                    </Button>
                ) : undefined
            }
        >
            <div className='flex flex-col gap-4 w-full'>
                <div className='flex flex-col gap-2 w-full'>
                    <h4 className='font-medium leading-[22px] text-base text-white'>Название</h4>
                    <p className='text-sm text-[var(--pixorion-gray-2)]'>{productName}</p>
                </div>
                <div className='flex flex-col gap-2 w-full'>
                    <h4 className='font-medium leading-[22px] text-base text-white'>Описание</h4>
                    <ExpandableText text={productDescription} markdown wordLimit={30} showCharacterCount />
                </div>
            </div>
        </CollapsibleCard>
    )
}
